import type { RepositoryDataSource } from '@/data/installations/constants';
import type { InstallationRepositoriesResponse } from '@/types/github';
import { useQueries, useQuery } from '@tanstack/react-query';

import { REPOSITORY_DATA_SOURCE } from '@/data/installations/constants';
import {
  getInstallationRepositoriesQueryFn,
  installationRepositoriesQueryKey,
} from '@/data/installations/queries';

/**
 * Hook to fetch repositories for a GitHub App installation.
 * @param installationId - The numeric ID of the GitHub installation. Must be a number, not a string.
 * @param source - Where to fetch the data from (github or db)
 */
export function useInstallationRepositories(
  installationId: number | null,
  source: RepositoryDataSource = REPOSITORY_DATA_SOURCE.GITHUB,
) {
  return useQuery<InstallationRepositoriesResponse>({
    queryKey: installationId
      ? installationRepositoriesQueryKey(installationId, source)
      : ['installation-repositories'],
    queryFn: () => getInstallationRepositoriesQueryFn(installationId!, source),
    enabled: Boolean(installationId && source),
    staleTime: Infinity, // Never consider data stale - consistent with useInstallations
    gcTime: Infinity, // Never garbage collect the data
  });
}

/**
 * Hook to fetch repositories for multiple GitHub App installations.
 * @param installationIds - Array of numeric IDs of GitHub installations
 * @param source - Where to fetch the data from (github or db)
 * @returns Object with combined results, loading and error states
 */
export function useAllInstallationRepositories(
  installationIds: number[] | undefined,
  source: RepositoryDataSource = REPOSITORY_DATA_SOURCE.GITHUB,
) {
  const queries = useQueries({
    queries: (installationIds || []).map((id) => ({
      queryKey: installationRepositoriesQueryKey(id, source),
      queryFn: () => getInstallationRepositoriesQueryFn(id, source),
      enabled: Boolean(id && source),
      staleTime: Infinity, // Never consider data stale
      gcTime: Infinity, // Never garbage collect the data
    })),
  });

  // Derived states for all queries
  // isLoading: true only during initial load when we have no data
  const isLoading = queries.some((query) => query.isLoading && !query.data);
  // An installation counts as failed only when it has no data: a failed refetch keeps the
  // cached repositories, which stay usable.
  const hasFailedWithoutData = (query: (typeof queries)[number] | undefined) =>
    !!query?.isError && !query.data;
  // isError: true only when every installation failed. One inaccessible installation
  // must not hide the repositories of the others; consumers scoped to one installation
  // should check failedInstallationIds instead.
  const isError = queries.length > 0 && queries.every(hasFailedWithoutData);
  const failedInstallationIds = (installationIds || []).filter((_, index) =>
    hasFailedWithoutData(queries[index]),
  );

  // Combine all repository data
  const combinedData = queries.reduce<InstallationRepositoriesResponse>(
    (acc, query) => {
      if (query.data) {
        return {
          total_count: acc.total_count + query.data.total_count,
          repositories: [...acc.repositories, ...query.data.repositories],
          repository_selection: acc.repository_selection,
        };
      }
      return acc;
    },
    { total_count: 0, repositories: [], repository_selection: 'all' },
  );

  return {
    data: combinedData,
    isLoading,
    isError,
    failedInstallationIds,
    queries, // Return individual queries for more granular access if needed
  };
}
